/** XeAgents.ai**/
import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Settings, Search, Server, Sliders, Activity, BarChart3, CreditCard, Package, UserPlus, HelpCircle, Building2, CalendarDays } from "lucide-react";
import { useTranslation } from "react-i18next";
import GlobalSettings from "./GlobalSettings";
import SEOModule from "./SEOModule";
import AnalyticsModule from "./AnalyticsModule";
import ElevenLabsPool from "./ElevenLabsPool";
import AutoRestartModule from "./AutoRestartModule";
import SystemSettings from "./SystemSettings";
import PlanManagement from "./PlanManagement";
import CreditPackageManagement from "./CreditPackageManagement";
import OwnerOnboarding from "./OwnerOnboarding";
import EnterpriseOnboarding from "./EnterpriseOnboarding";
import EnterpriseBillingSettings from "./EnterpriseBillingSettings";
import SupportKBManagement from "./SupportKBManagement";
import ZoneCreditSettings from "./ZoneCreditSettings";
import CalendarIntegrationSettings from "./CalendarIntegrationSettings";

interface SettingsPageProps {
  onSwitchTab?: (tab: string) => void;
  initialSubTab?: string;
  /** Pilot request to onboard, handed straight to the owner onboarding form. */
  pilotRequestToOnboard?: string | null;
  onPilotRequestConsumed?: () => void;
}

export default function SettingsPage({
  onSwitchTab,
  initialSubTab,
  pilotRequestToOnboard,
  onPilotRequestConsumed,
}: SettingsPageProps) {
  const { t } = useTranslation();
  const [activeSubTab, setActiveSubTab] = useState(initialSubTab || "master");

  // The parent can ask for a sub-tab after this component is already mounted
  // (clicking a pilot request jumps straight to onboarding), so follow the prop
  // rather than only reading it once as an initial value.
  useEffect(() => {
    if (initialSubTab) setActiveSubTab(initialSubTab);
  }, [initialSubTab]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">{t("admin.settings.title") || "Settings"}</h2>
        <p className="text-muted-foreground">
          {t("admin.settings.description") || "Configure platform settings, SEO, and integrations"}
        </p>
      </div>

      <Tabs value={activeSubTab} onValueChange={setActiveSubTab} className="space-y-4">
        <TabsList className="grid grid-cols-11 w-full max-w-7xl">
          <TabsTrigger value="master" data-testid="settings-tab-master" className="flex items-center gap-2">
            <Sliders className="h-4 w-4" />
            <span className="hidden sm:inline">{t("admin.settings.tabs.master")}</span>
            <span className="sm:hidden">Master</span>
          </TabsTrigger>
          <TabsTrigger value="plans" data-testid="settings-tab-plans" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">Plans</span>
            <span className="sm:hidden">Plans</span>
          </TabsTrigger>
          <TabsTrigger value="credit-packages" data-testid="settings-tab-credit-packages" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            <span className="hidden sm:inline">Credits</span>
            <span className="sm:hidden">Credits</span>
          </TabsTrigger>
          <TabsTrigger value="elevenlabs" data-testid="settings-tab-elevenlabs" className="flex items-center gap-2">
            <Server className="h-4 w-4" />
            <span className="hidden sm:inline">{t("admin.settings.tabs.elevenlabs")}</span>
            <span className="sm:hidden">ElevenLabs</span>
          </TabsTrigger>
          <TabsTrigger value="seo" data-testid="settings-tab-seo" className="flex items-center gap-2">
            <Search className="h-4 w-4" />
            <span className="hidden sm:inline">{t("admin.settings.tabs.seo")}</span>
            <span className="sm:hidden">SEO</span>
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="settings-tab-analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">{t("admin.settings.tabs.analytics") || "Analytics"}</span>
            <span className="sm:hidden">Analytics</span>
          </TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="settings-tab-onboarding" className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            <span className="hidden sm:inline">Onboarding</span>
            <span className="sm:hidden">Onboard</span>
          </TabsTrigger>
          <TabsTrigger value="enterprise" data-testid="settings-tab-enterprise" className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            <span className="hidden sm:inline">Enterprise</span>
            <span className="sm:hidden">Enterprise</span>
          </TabsTrigger>
          <TabsTrigger value="support-kb" data-testid="settings-tab-support-kb" className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Support KB</span>
            <span className="sm:hidden">Support</span>
          </TabsTrigger>
          <TabsTrigger value="calendar-integration" data-testid="settings-tab-calendar-integration" className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            <span className="hidden sm:inline">Calendar</span>
            <span className="sm:hidden">Calendar</span>
          </TabsTrigger>
          <TabsTrigger value="system" data-testid="settings-tab-system" className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            <span className="hidden sm:inline">{t("admin.settings.tabs.system")}</span>
            <span className="sm:hidden">System</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="master" className="space-y-4">
          <GlobalSettings onSwitchTab={(tab) => {
            if (tab === "elevenlabs") {
              setActiveSubTab("elevenlabs");
            } else {
              onSwitchTab?.(tab);
            }
          }} />
        </TabsContent>

        <TabsContent value="plans" className="space-y-4">
          <PlanManagement />
        </TabsContent>

        <TabsContent value="credit-packages" className="space-y-6">
          <CreditPackageManagement />
          <ZoneCreditSettings />
        </TabsContent>

        <TabsContent value="elevenlabs" className="space-y-4">
          <ElevenLabsPool />
        </TabsContent>

        <TabsContent value="seo" className="space-y-4">
          <SEOModule />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <AnalyticsModule />
        </TabsContent>

        <TabsContent value="onboarding" className="space-y-4">
          <OwnerOnboarding
            initialPilotRequestId={pilotRequestToOnboard}
            onInitialPilotRequestConsumed={onPilotRequestConsumed}
          />
        </TabsContent>

        <TabsContent value="enterprise" className="space-y-6">
          <EnterpriseOnboarding />
          <EnterpriseBillingSettings />
        </TabsContent>

        <TabsContent value="support-kb" className="space-y-4">
          <SupportKBManagement />
        </TabsContent>

        <TabsContent value="calendar-integration" className="space-y-4">
          <CalendarIntegrationSettings />
        </TabsContent>

        <TabsContent value="system" className="space-y-6">
          <SystemSettings />
          <AutoRestartModule />
        </TabsContent>
      </Tabs>
    </div>
  );
}
